import { Email, NotificationAdd, Visibility } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { DownloadButton } from "@repo/shared/components";
import {
	DunningStatus,
	useDownloadDocumentLazyQuery,
	useGetAllDunningCasesByDocumentLazyQuery,
} from "@repo/server-api";
import { formatCurrencyNumber, formatDate } from "@repo/legacy/components/helper/Index.js";
import {
	PreviewDocumentDialog,
	type PreviewDocumentDialogHandle,
} from "@repo/legacy/dialogs/PreviewDocumentForwardRefDialog";
import { useTranslation } from "next-i18next";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { CommercialDocumentSummary } from "@repo/shared-invoice/types";

export type DunningCasesHistoryDialogHandle = {
	show: (invoice: CommercialDocumentSummary) => void;
	close: () => void;
	refresh: () => void;
};

type DunningCasesHistoryDialogProps = {
	onGenerateReminder: (invoice: CommercialDocumentSummary) => void;
	onSendEntry: (invoice: CommercialDocumentSummary, entryId: string) => void;
};

export const DunningCasesHistoryDialog = forwardRef<
	DunningCasesHistoryDialogHandle,
	DunningCasesHistoryDialogProps
>(({ onGenerateReminder, onSendEntry }, ref) => {
	const { t, i18n } = useTranslation();

	const [open, setOpen] = useState(false);
	const [invoice, setInvoice] = useState<CommercialDocumentSummary | null>(null);
	const invoiceRef = useRef<CommercialDocumentSummary | null>(null);
	const previewRef = useRef<PreviewDocumentDialogHandle | null>(null);

	const [fetchDunningCases, { data, loading }] = useGetAllDunningCasesByDocumentLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [downloadDocument] = useDownloadDocumentLazyQuery({
		fetchPolicy: "no-cache",
	});

	useImperativeHandle(
		ref,
		() => ({
			show: (inv: CommercialDocumentSummary) => {
				invoiceRef.current = inv;
				setInvoice(inv);
				setOpen(true);
			},
			close: () => {
				handleClose();
			},
			refresh: () => {
				if (invoiceRef.current?.id) {
					fetchDunningCases({ variables: { id: invoiceRef.current.id } });
				}
			},
		}),
		[fetchDunningCases],
	);

	useEffect(() => {
		if (open && invoice?.id) {
			invoiceRef.current = invoice;
			fetchDunningCases({ variables: { id: invoice.id } });
		}
	}, [open, invoice?.id, fetchDunningCases]);

	const handleClose = useCallback(() => {
		setOpen(false);
		setInvoice(null);
		invoiceRef.current = null;
	}, []);

	const dunningCase = data?.dunningCaseByDocumentId;
	const entries = dunningCase?.entries ?? [];

	const handleGenerateReminder = () => {
		if (invoice) {
			onGenerateReminder(invoice);
		}
	};

	const handlePreview = async (entryId: string) => {
		if (!invoiceRef.current?.id) return;
		const result = await downloadDocument({
			variables: { id: invoiceRef.current.id, shouldDownload: false, entityId: entryId },
		});
		const url = result.data?.downloadDocument;
		if (url) previewRef.current?.show({ url });
	};

	const handleDownload = async (entryId: string) => {
		if (!invoiceRef.current?.id) return;
		const result = await downloadDocument({
			variables: { id: invoiceRef.current.id, shouldDownload: true, entityId: entryId },
		});
		const url = result.data?.downloadDocument;
		if (url) window.open(url, "_blank");
	};

	const handleSend = (entryId: string) => {
		if (invoice) {
			onSendEntry(invoice, entryId);
		}
	};

	const getStatusLabel = (status: DunningStatus): string => {
		switch (status) {
			case DunningStatus.Open:
				return t("dunning-status.open");
			case DunningStatus.Closed:
				return t("dunning-status.closed");
			case DunningStatus.Expired:
				return t("dunning-status.expired");
			case DunningStatus.Sending:
				return t("dunning-status.sending");
			case DunningStatus.Sent:
				return t("dunning-status.sent");
			default:
				return status;
		}
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="lg"
				fullWidth
				data-testid="dunning-cases-history-dialog">
				<DialogTitle
					style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<span>{t("dunning-history")}</span>
					<Button
						variant="contained"
						className="greenButton"
						startIcon={<NotificationAdd />}
						data-testid="generate-new-reminder-button"
						onClick={handleGenerateReminder}>
						{t("generate-new-reminder")}
					</Button>
				</DialogTitle>
				<DialogContent>
					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
							<CircularProgress />
							<Typography sx={{ ml: 2 }}>{t("loading")}</Typography>
						</Box>
					) : dunningCase ? (
						<>
							<Box sx={{ mb: 2, display: "flex", gap: 4 }}>
								<Typography variant="body2">
									<strong>{t("accrued-fees")}:</strong>{" "}
									{formatCurrencyNumber(dunningCase.accruedFees)}
								</Typography>
							</Box>
							{entries.length === 0 ? (
								<Typography>{t("no-dunning-history")}</Typography>
							) : (
								<TableContainer>
									<Table size="small" data-testid="dunning-history-table">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 600 }}>{t("level")}</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>{t("sent-date")}</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>{t("due-date")}</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>{t("fee-amount")}</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>{t("status")}</TableCell>
												<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
													{t("actions")}
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{entries.map((entry) => (
												<TableRow
													key={entry.id}
													sx={{
														"&:nth-of-type(odd)": {
															backgroundColor: "action.hover",
														},
													}}>
													<TableCell>{entry.level}</TableCell>
													<TableCell>{formatDate(entry.sentDate ?? null, i18n.language)}</TableCell>
													<TableCell>{formatDate(entry.dueDate, i18n.language)}</TableCell>
													<TableCell>{formatCurrencyNumber(entry.fee)}</TableCell>
													<TableCell>{getStatusLabel(entry.status)}</TableCell>
													<TableCell align="center">
														<Tooltip title={t("preview")}>
															<IconButton
																size="small"
																data-testid={`preview-button-${entry.id}`}
																onClick={() => handlePreview(entry.id)}>
																<Visibility />
															</IconButton>
														</Tooltip>
														<DownloadButton
															onDownload={() => handleDownload(entry.id)}
															title={t("download")}
															buttonProps={{ size: "small" }}
														/>
														<Tooltip title={t("send")}>
															<IconButton
																size="small"
																data-testid={`send-button-${entry.id}`}
																onClick={() => handleSend(entry.id)}>
																<Email />
															</IconButton>
														</Tooltip>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</>
					) : (
						<Typography>{t("no-dunning-history")}</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} data-testid="dunning-history-close-button">
						{t("close")}
					</Button>
				</DialogActions>
			</Dialog>
			<PreviewDocumentDialog ref={previewRef} />
		</>
	);
});
